<template>
    <number-field
        name="fiatAmount"
        required
        .placeholder="t('widget.dollar-cost-average.fiat-amount.placeholder', { amount: formatFiat(minimum) })"
        :value="state.value ?? null"
        :min="minimum"
        :max="maximum"
        @created="updateAmount"
        @updated="updateAmount"
    >
        <template #label>{{ label }}</template>

        <template #append>
            <currency-select type="fiat" :show-search="false" :value="selectedCurrencyCode" @created="initCurrency" @updated="selectCurrency">
                <template #opener="{ open, quickSelect }">
                    <div class="opener background" @click="selectEnabled ? open : null" @keydown="quickSelect">
                        <euro-icon class="icon-euro" />
                        {{ selectedCurrencyCode }}
                        <caret-icon v-if="selectEnabled" direction="down" />
                    </div>
                </template>

                <template #header="{ close }">
                    <view-header close-button @closed="close">{{ t('widget.dollar-cost-average.fiat-amount.modal.title') }}</view-header>
                </template>
            </currency-select>
        </template>

        <template #required>{{ t('widget.field-error.required') }}</template>
        <template #min>{{ t('widget.dollar-cost-average.fiat-amount.field-error.minimum-amount', { amount: formatFiat(minimum) }) }}</template>
        <template #max>{{ t('widget.dollar-cost-average.fiat-amount.field-error.maximum-amount', { amount: formatFiat(maximum) }) }}</template>
    </number-field>
</template>

<script lang="ts" setup>
import CurrencySelect from '@/dollar-cost-average/components/currency-select.vue';
import { ViewHeader } from '@btcdirect/components';
import { CaretIcon, EuroIcon } from '@btcdirect/components/icons';
import { useNumberFormat, useTranslations } from '@btcdirect/composables';
import { useCurrenciesStore } from '@btcdirect/stores';
import { NumberField, ValidatedNumberFieldData, ValidatedStringFieldData } from 'out-of-the-vue';
import { computed, onMounted, reactive, ref, watch } from 'vue';

const emit = defineEmits<{ (event: 'created' | 'updated', data: ValidatedNumberFieldData | ValidatedStringFieldData): void }>();

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    value: {
        type: Number,
        required: false,
        default: null
    },
    cryptoCurrency: {
        type: String,
        required: false,
        default: 'BTC'
    },
    minimum: {
        type: Number,
        required: true
    },
    maximum: {
        type: Number,
        required: true
    }
});

const currenciesStore = useCurrenciesStore();
const { formatFiat } = useNumberFormat();
const { t } = useTranslations();

const state = reactive<ValidatedNumberFieldData>({
    name: 'fiatAmount',
    value: props.value,
    valid: true,
    failed: null
});

watch(
    () => props.value,
    (received: number) => {
        state.value = received;
    }
);

const selectedCurrencyCode = ref<string>('EUR');

const updateAmount = (data: ValidatedNumberFieldData): void => {
    state.value = data.value;
    state.valid = data.valid;
    state.failed = data.failed;

    emit('updated', { ...state });
};

const initCurrency = (data: ValidatedStringFieldData): void => {
    selectedCurrencyCode.value = data.value;
    emit('created', Object.assign({}, data, { name: 'fiatCurrency' }));
};

const selectCurrency = (data: ValidatedStringFieldData): void => {
    selectedCurrencyCode.value = data.value;
    emit('updated', Object.assign({}, data, { name: 'fiatCurrency' }));
};

const selectEnabled = computed<boolean>(() => currenciesStore.fiatCurrencies?.length > 1);

onMounted(() => {
    emit('created', state);
});
</script>

<style lang="scss" scoped>
@use '@@scss/flex';
@use '@@scss/fonts';

.opener {
    @include flex.flex;
    @include flex.align(center);
    @include fonts.gt-walsheim-medium;
    gap: 0.5rem;

    &:hover {
        cursor: pointer;
    }
}
</style>
